/// <reference types="vite/client" />
import { Suspense, lazy, useEffect, useMemo } from 'react'
import { FrontDoor, RESET_PATH } from '../game/door/FrontDoor'
import { HeadStart } from '../game/headStart'
import { Loading, Unreachable } from '../game/Loading'
import { isSandboxPath } from '../game/paths'
import { usePath } from './routing'
import { useSession } from './useSession'

// The flag guards the import itself, so a production build drops the sandbox,
// and the three.js it pulls in, rather than shipping a chunk nobody can reach.
const Sandbox = import.meta.env.DEV ? lazy(() => import('../stageville/sandbox')) : null
/** The game, its own chunk, so the first paint is the loading screen and not three.js. */
const Game = lazy(() => import('../game/Game'))

/** The game is the whole app. The sandbox sits beside it in development, drawing from fixtures. */
export function App() {
  const { state, refresh } = useSession()
  const path = usePath()
  const account = state.status === 'signed-in' ? state.me.id : null
  // One head start for each account signed in, so a reset that signs another one in never shows the first one's answers.
  const headStart = useMemo(() => new HeadStart(), [account])
  const playing = account !== null && !(Sandbox && isSandboxPath(path)) && path !== RESET_PATH
  // The game's first asks and its world's code begin as the game is about to show, so they come in while its code does.
  useEffect(() => {
    if (playing) headStart.begin()
  }, [playing, headStart])

  // Above the session gate: the sandbox must not wait on a server that may not be running.
  if (Sandbox && isSandboxPath(path)) {
    return (
      <Suspense fallback={null}>
        <Sandbox path={path} />
      </Suspense>
    )
  }

  // The mailed reset link works signed in or out, and needs no session to show.
  if (path === RESET_PATH) return <FrontDoor onSignedIn={refresh} />
  if (state.status === 'loading') return <Loading />
  if (state.status === 'unreachable') return <Unreachable detail={state.detail} onRetry={() => void refresh()} />
  if (state.status === 'signed-out') return <FrontDoor onSignedIn={refresh} />
  return (
    <Suspense fallback={<Loading />}>
      <Game me={state.me} path={path} headStart={headStart} />
    </Suspense>
  )
}
